import React, { useMemo } from "react";
import {
	Bar,
	CartesianGrid,
	ComposedChart,
	Legend,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { formatAxisLabel, formatMetricValue } from "./partnerAnalyticsUtils";

const FALLBACK_COLOR = "#6B7280";

const fieldKey = (partnerId, metricKey) => `${partnerId}__${metricKey}`;

const MetricTooltip = ({ active, payload, label, partners, metrics, colorMap = {} }) => {
	if (!active || !payload?.length) return null;
	const row = payload[0]?.payload;

	return (
		<div
			style={{
				background: "#fff",
				border: "1px solid #E5E7EB",
				borderRadius: 8,
				padding: "10px 14px",
				fontSize: 13,
				boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
				maxWidth: 280,
			}}
		>
			<p style={{ fontWeight: 600, marginBottom: 8, color: "#111" }}>{label}</p>
			{partners.map((p) => {
				const baseColor = colorMap[p.id] || FALLBACK_COLOR;
				const rows = metrics
					.map((m) => ({ m, val: row?.[fieldKey(p.id, m.key)] }))
					.filter(({ val }) => val != null && val !== 0);
				const cohortClicks = row?.[`${p.id}__cohortClicks`];
				const cohortEarnedClicks = row?.[`${p.id}__cohortEarnedClicks`];
				if (rows.length === 0 && !cohortClicks && !cohortEarnedClicks) return null;

				return (
					<div key={p.id} style={{ marginBottom: 10 }}>
						<p style={{ margin: "0 0 4px 0", fontSize: 12, fontWeight: 700, color: baseColor }}>
							{p.name}
						</p>
						{rows.map(({ m, val }) => (
							<p key={m.key} style={{ color: "#4B5563", margin: "2px 0", fontSize: 12 }}>
								{m.label}: <strong>{formatMetricValue(val, m.valueType)}</strong>
							</p>
						))}
						{cohortClicks != null && cohortClicks !== 0 && (
							<p style={{ color: baseColor, opacity: 0.75, margin: "2px 0 0 0", fontSize: 11 }}>
								↳ Cohort clicks: <strong>{formatMetricValue(cohortClicks, "number")}</strong>
							</p>
						)}
						{cohortEarnedClicks != null && cohortEarnedClicks !== 0 && (
							<p style={{ color: baseColor, opacity: 0.75, margin: "2px 0 0 0", fontSize: 11 }}>
								↳ Cohort earned clicks: <strong>{formatMetricValue(cohortEarnedClicks, "number")}</strong>
							</p>
						)}
					</div>
				);
			})}
		</div>
	);
};

/**
 * Grouped bar chart for a primary metric (e.g. Clicks) — same style as
 * UsersEarningRangePanel. Extra related metrics (e.g. Earned Clicks) can be
 * passed with `bar: false` — they don't get their own column, they're only
 * surfaced in the hover tooltip alongside the primary metric's value.
 *
 * `cohortSeries` (the registered-users series) carries its own nested
 * clicks/earnedClicks breakdown for the same-day cohort — also tooltip-only.
 */
const PartnerMetricChart = ({
	title,
	subtitle,
	icon,
	labels = [],
	metrics = [],
	cohortSeries = [],
	colorMap = {},
	mode,
	height = 340,
	visiblePartnerIds = null,
}) => {
	const partners = useMemo(() => {
		const byId = new Map();
		metrics.forEach((m) => {
			(m.series || []).forEach((s) => {
				if (!s?.id) return;
				if (visiblePartnerIds && !visiblePartnerIds.has(s.id)) return;
				if (!byId.has(s.id)) byId.set(s.id, { id: s.id, name: s.name });
			});
		});
		return Array.from(byId.values());
	}, [metrics, visiblePartnerIds]);

	const seriesByMetric = useMemo(() => {
		const map = {};
		metrics.forEach((m) => {
			const byId = {};
			(m.series || []).forEach((s) => {
				byId[s.id] = s;
			});
			map[m.key] = byId;
		});
		return map;
	}, [metrics]);

	const cohortByPartner = useMemo(() => {
		const byId = {};
		cohortSeries.forEach((s) => {
			if (s?.id) byId[s.id] = s;
		});
		return byId;
	}, [cohortSeries]);

	const chartData = useMemo(
		() =>
			labels.map((rawLabel, idx) => {
				const row = { name: formatAxisLabel(rawLabel, mode) };
				partners.forEach((p) => {
					metrics.forEach((m) => {
						row[fieldKey(p.id, m.key)] = seriesByMetric[m.key]?.[p.id]?.data?.[idx] ?? 0;
					});
					row[`${p.id}__cohortClicks`] = cohortByPartner[p.id]?.extra?.clicks?.[idx] ?? null;
					row[`${p.id}__cohortEarnedClicks`] = cohortByPartner[p.id]?.extra?.earnedClicks?.[idx] ?? null;
				});
				return row;
			}),
		[labels, partners, metrics, seriesByMetric, cohortByPartner, mode]
	);

	const hasAnyData = chartData.some((row) =>
		partners.some((p) => metrics.some((m) => row[fieldKey(p.id, m.key)]))
	);

	// Only metrics not explicitly opted out (`bar: false`) get their own
	// column — anything else (e.g. Earned Clicks) is tooltip-only, keeping
	// the chart to a single bar per partner.
	const barMetrics = useMemo(() => metrics.filter((m) => m.bar !== false), [metrics]);

	const partnerCount = partners.length;
	const barSize = partnerCount <= 1 ? 40 : partnerCount <= 3 ? 22 : partnerCount <= 6 ? 14 : 10;

	return (
		<div className="admin-form-container-v4" style={{ marginBottom: "var(--spacing-xl)" }}>
			<div style={{ marginBottom: 8 }}>
				<h3 style={{ margin: 0 }}>
					{icon ? <span style={{ marginRight: 8 }}>{icon}</span> : null}
					{title}
				</h3>
				{subtitle && (
					<p style={{ fontSize: 13, color: "var(--text-muted)", margin: "4px 0 0 0" }}>{subtitle}</p>
				)}
			</div>

			{!hasAnyData ? (
				<div style={{ padding: "48px 0", textAlign: "center", color: "var(--text-muted)", fontSize: 14 }}>
					No data available for the selected filters.
				</div>
			) : (
				<ResponsiveContainer width="100%" height={height}>
					<ComposedChart
						data={chartData}
						margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
						barCategoryGap={partners.length > 1 ? "18%" : "28%"}
						barGap={2}
					>
						<CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
						<XAxis
							dataKey="name"
							tick={{ fontSize: 12 }}
							interval="preserveStartEnd"
							minTickGap={24}
						/>
						<YAxis tick={{ fontSize: 12 }} width={48} />
						<Tooltip content={<MetricTooltip partners={partners} metrics={metrics} colorMap={colorMap} />} />
						<Legend
							wrapperStyle={{ fontSize: 12 }}
							payload={partners.map((p) => ({
								value: p.name,
								type: "square",
								color: colorMap[p.id] || FALLBACK_COLOR,
								id: p.id,
							}))}
						/>
						{partners.map((p) =>
							barMetrics.map((m) => (
								<Bar
									key={fieldKey(p.id, m.key)}
									dataKey={fieldKey(p.id, m.key)}
									name={p.name}
									fill={colorMap[p.id] || FALLBACK_COLOR}
									maxBarSize={barSize}
								/>
							))
						)}
					</ComposedChart>
				</ResponsiveContainer>
			)}
		</div>
	);
};

export default PartnerMetricChart;
